import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../api/client";
import type { Analyze, AnalyzeCriterion, AnalyzeKind } from "../api/types";
import { ErrorBox, Loading, relativeTime } from "../components/common";
import { useI18n } from "../i18n";

const KINDS: AnalyzeKind[] = ["language", "app", "country"];

const DATALIST_ID: Partial<Record<AnalyzeKind, string>> = {
  app: "datalist-packages",
  language: "datalist-languages",
  country: "datalist-countries",
};

const hintFor = (kind: AnalyzeKind): string => {
  switch (kind) {
    case "language":
      return "en-US or en (en matches en-GB too)";
    case "app":
      return "com.instagram.android";
    case "country":
      return "US";
  }
};

const freshCriteria = (): AnalyzeCriterion[] => [{ kind: "language", value: "" }];

export default function Analyzes() {
  const { t } = useI18n();
  const devices = useQuery({ queryKey: ["devices"], queryFn: () => api.devices() });
  const fleet = devices.data ?? [];

  // The fleet already knows its package names, locales and countries —
  // suggesting them saves typing exact package ids from memory.
  const known = {
    packages: [...new Set(fleet.flatMap((d) => d.installed_packages ?? []))].sort(),
    languages: [...new Set(fleet.flatMap((d) => d.languages ?? []))].sort(),
    countries: [...new Set(fleet.map((d) => d.country).filter((c): c is string => !!c))].sort(),
  };

  return (
    <>
      <datalist id="datalist-packages">
        {known.packages.map((pkg) => (
          <option key={pkg} value={pkg} />
        ))}
      </datalist>
      <datalist id="datalist-languages">
        {known.languages.map((lang) => (
          <option key={lang} value={lang} />
        ))}
      </datalist>
      <datalist id="datalist-countries">
        {known.countries.map((country) => (
          <option key={country} value={country} />
        ))}
      </datalist>

      <div className="page-header">
        <h2>{t("analyzes.title")}</h2>
      </div>

      <div className="col">
        <div className="card">
          <strong>{t("analyzes.setTitle")}</strong>
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            {t("analyzes.setIntro")}
          </p>
          <NewAnalyze />
          <AnalyzeList />
        </div>
      </div>
    </>
  );
}

function NewAnalyze() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [criteria, setCriteria] = useState<AnalyzeCriterion[]>(freshCriteria());

  const create = useMutation({
    mutationFn: () => api.createAnalyze({ name, criteria }),
    onSuccess: () => {
      setName("");
      setCriteria(freshCriteria());
      queryClient.invalidateQueries({ queryKey: ["analyzes"] });
      queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
  });

  const valid =
    name.trim() !== "" &&
    criteria.length > 0 &&
    criteria.every((criterion) => criterion.value.trim() !== "");

  const setCriterion = (index: number, patch: Partial<AnalyzeCriterion>) =>
    setCriteria(criteria.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  return (
    <>
      <div className="row" style={{ margin: "8px 0", flexWrap: "wrap" }}>
        <input
          placeholder={t("analyzes.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ maxWidth: 260 }}
        />
        {criteria.map((criterion, index) => (
          <span key={index} className="row" style={{ margin: 0 }}>
            <select
              value={criterion.kind}
              onChange={(e) => setCriterion(index, { kind: e.target.value as AnalyzeKind })}
              style={{ maxWidth: 160 }}
            >
              {KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`kind.${kind}`)}
                </option>
              ))}
            </select>
            <input
              className="mono"
              placeholder={hintFor(criterion.kind)}
              value={criterion.value}
              list={DATALIST_ID[criterion.kind]}
              onChange={(e) => setCriterion(index, { value: e.target.value })}
            />
            {criteria.length > 1 && (
              <button
                title={t("analyzes.removeCondition")}
                onClick={() => setCriteria(criteria.filter((_, i) => i !== index))}
              >
                ×
              </button>
            )}
          </span>
        ))}
        <button
          onClick={() => setCriteria([...criteria, { kind: "language", value: "" }])}
          title={t("analyzes.addConditionTitle")}
        >
          {t("analyzes.addCondition")}
        </button>
        <button className="primary" disabled={!valid || create.isPending} onClick={() => create.mutate()}>
          {t("analyzes.create")}
        </button>
      </div>
      {create.error && <ErrorBox error={create.error} />}
      {criteria.length > 1 && (
        <p className="muted" style={{ fontSize: 12, margin: "0 0 8px" }}>
          {t("analyzes.allConditionsNote")}
        </p>
      )}
    </>
  );
}

function AnalyzeList() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const analyzes = useQuery({ queryKey: ["analyzes"], queryFn: api.analyzes });
  const devices = useQuery({ queryKey: ["devices"], queryFn: () => api.devices() });

  const toggle = useMutation({
    mutationFn: (analyze: Analyze) => api.updateAnalyze(analyze.id, { enabled: !analyze.enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["analyzes"] }),
  });
  const remove = useMutation({
    mutationFn: (analyze: Analyze) => {
      if (!window.confirm(t("analyzes.deleteConfirm", { name: analyze.name })))
        return Promise.resolve(null);
      return api.deleteAnalyze(analyze.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["analyzes"] });
      queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
  });

  if (analyzes.isLoading) return <Loading what={t("loading.analyzes")} />;
  if (analyzes.error) return <ErrorBox error={analyzes.error} />;

  const fleet = devices.data ?? [];

  return (
    <>
      {toggle.error && <ErrorBox error={toggle.error} />}
      {remove.error && <ErrorBox error={remove.error} />}
      <table>
        <thead>
          <tr>
            <th>{t("analyzes.th.name")}</th>
            <th>{t("analyzes.th.checks")}</th>
            <th>{t("analyzes.th.matching")}</th>
            <th>{t("analyzes.th.created")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(analyzes.data ?? []).map((analyze) => {
            const matching = fleet.filter((d) => d.analyze_matches[analyze.id]).length;
            return (
              <tr key={analyze.id} style={!analyze.enabled ? { opacity: 0.5 } : undefined}>
                <td>{analyze.name}</td>
                <td>
                  <span className="muted">
                    {analyze.criteria.map((criterion, index) => (
                      <span key={index}>
                        {index > 0 && <span> + </span>}
                        {t(`kindShort.${criterion.kind}`)}{" "}
                        <code className="mono">{criterion.value}</code>
                      </span>
                    ))}
                  </span>
                </td>
                <td className="muted">
                  {analyze.enabled
                    ? t("analyzes.matching", { matching, total: fleet.length })
                    : t("analyzes.disabled")}
                </td>
                <td className="muted">{relativeTime(analyze.created_at, t)}</td>
                <td>
                  <span className="row" style={{ justifyContent: "flex-end", margin: 0 }}>
                    <button
                      className={`toggle ${analyze.enabled ? "on" : "off"}`}
                      disabled={toggle.isPending}
                      onClick={() => toggle.mutate(analyze)}
                      title={
                        analyze.enabled
                          ? t("analyzes.toggleOffTitle")
                          : t("analyzes.toggleOnTitle")
                      }
                    >
                      {analyze.enabled ? t("analyzes.on") : t("analyzes.off")}
                    </button>
                    <button className="danger" onClick={() => remove.mutate(analyze)}>
                      {t("settings.delete")}
                    </button>
                  </span>
                </td>
              </tr>
            );
          })}
          {(analyzes.data ?? []).length === 0 && (
            <tr>
              <td colSpan={5} className="muted">
                {t("analyzes.empty")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}
